import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/store";
import { Shield, Plus, Edit, Trash2, RefreshCw } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { RootState } from "../../redux/store";
import { fetchChatRules, deleteChatRule } from "../../redux/Slices/ChatRulesSlice";
import { ChatRule, ChatRuleTypeEnum, ChatRuleStatusEnum } from "../../modules/shared/model";
import ChatRuleModal from "./ChatRuleModal";

const ChatRules = () => {
  const dispatch = useAppDispatch();
  const { chatRules, loading, error } = useSelector((state: RootState) => state.chatRules);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ChatRule | null>(null);

  useEffect(() => {
    dispatch(fetchChatRules({}));
  }, [dispatch]);

  const handleAddNewRule = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  const handleEditRule = (rule: ChatRule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (window.confirm("Are you sure you want to delete this rule?")) {
      dispatch(deleteChatRule(ruleId));
    }
  };

  const getRuleTypeDescription = (typeId: number) => {
    const descriptions = {
      [ChatRuleTypeEnum.No_Contact_Information]: "blocks messages containing phone numbers, email addresses, or other external contact details.",
      [ChatRuleTypeEnum.Professional_Language]: "flags messages containing inappropriate or unprofessional language.",
      [ChatRuleTypeEnum.External_Platform_Links]: "blocks messages containing links to external platforms or services.",
      [ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter]: "block messages sent in rapid succession or containing spam (repeated keywords, suspicious content).",
      [ChatRuleTypeEnum.Sensitive_Content_Filter]: "detect and block words or phrases related to prohibited content (violence, discrimination, pornography).",
      [ChatRuleTypeEnum.Chat_Message_Limit]: "prevent flooding by limiting the number of messages sent within a defined time frame.",
      [ChatRuleTypeEnum.External_Deal_Detection]: "intercept messages attempting to move negotiations outside the platform (e.g., \"I'll pay you via PayPal\" or \"message me on Telegram\").",
      [ChatRuleTypeEnum.File_Attachment_Filter]: "block or restrict sending certain file types (e.g., executables, zip files, or potentially harmful content).",
      [ChatRuleTypeEnum.Role_Based_Contextual_Rules]: "apply different rules depending on whether the message is sent by a customer or by a professional."
    };
    return descriptions[typeId as keyof typeof descriptions] || "Unknown rule type";
  };

  const getRuleTypeLabel = (typeId: number) => {
    const labels = {
      [ChatRuleTypeEnum.No_Contact_Information]: "No Contact Information",
      [ChatRuleTypeEnum.Professional_Language]: "Professional Language",
      [ChatRuleTypeEnum.External_Platform_Links]: "External Platform Links",
      [ChatRuleTypeEnum.Spam_and_Repetitive_Messages_Filter]: "Spam and Repetitive Messages Filter",
      [ChatRuleTypeEnum.Sensitive_Content_Filter]: "Sensitive Content Filter",
      [ChatRuleTypeEnum.Chat_Message_Limit]: "Chat Message Limit",
      [ChatRuleTypeEnum.External_Deal_Detection]: "External Deal Detection",
      [ChatRuleTypeEnum.File_Attachment_Filter]: "File Attachment Filter",
      [ChatRuleTypeEnum.Role_Based_Contextual_Rules]: "Role-Based Contextual Rules"
    };
    return labels[typeId as keyof typeof labels] || "Unknown";
  };

  const getStatusLabel = (statusId: number) => {
    return statusId === ChatRuleStatusEnum.Active ? "Active" : "Inactive";
  };

  if (loading && chatRules.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span>Loading chat rules...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" style={{ textAlign: "left" }}>
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <h2 className="text-lg font-semibold">Chat Rules</h2>
          <p className="text-gray-600 mt-1">Configure automated chat monitoring rules</p>
        </div>
        <Button 
          className="flex items-center gap-2 w-full sm:w-auto"
          onClick={handleAddNewRule}
        >
          <Plus className="h-4 w-4" />
          Add New Rule
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {chatRules.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-gray-500">No chat rules found. Create your first rule to get started.</p>
          </Card>
        ) : (
          chatRules.map(rule => (
            <Card key={rule.id} className="p-4">
              <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <h3 className="font-medium">{getRuleTypeLabel(rule.type.id)}</h3>
                    <Badge variant={undefined} className={Number(rule.status.id) === ChatRuleStatusEnum.Active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}>
                      {getStatusLabel(Number(rule.status.id))}
                    </Badge>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">{getRuleTypeDescription(rule.type.id)}</p>
                  <div className="mt-2 text-xs text-gray-500">
                    Created: {new Date(rule.createdAt).toLocaleDateString()}
                    {rule.updatedAt !== rule.createdAt && (
                      <span> • Updated: {new Date(rule.updatedAt).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditRule(rule)}
                    disabled={loading}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600"
                    onClick={() => handleDeleteRule(rule.id)}
                    disabled={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <ChatRuleModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRule(null);
        }}
        editingRule={editingRule}
      />
    </div>
  );
};

export default ChatRules;
